import {
  Box,
  BoxProps,
  ButtonGroup,
  HStack,
  Icon,
  IconButton,
  NumberInput,
  NumberInputField,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useState } from 'react';
import {
  RiCheckLine,
  RiCloseLine,
  RiDeleteBinLine,
  RiEditLine,
  RiEyeLine,
} from 'react-icons/ri';
import { ConfirmationModal } from '../../../../../../../components/ConfirmationModal';
import { DateRangePicker } from '../../../../../../../components/Form/DateRangePicker';
import { MoneyInput } from '../../../../../../../components/Form/MoneyInput';
import { LinkButton } from '../../../../../../../components/LinkButton';
import { Pagination } from '../../../../../../../components/Pagination';
import { TableFilters } from '../../../../../../../components/TableFilters';
import { useAuth } from '../../../../../../../hooks/auth';
import {
  maskMoney,
  maskShortDate,
} from '../../../../../../../utils/formatters/handleMask';

export interface IAppointmentProductTableItem {
  id?: string;
  costCenter: string;
  description?: string;
  finishAt?: string;
  name: string;
  price: number;
  productCode: string;
  productId: string;
  productNature: string;
  productType: string;
  invoiceType: 'NF' | 'ND';
  quantity: number;
  startAt?: string;
  createdAt: string;
  formattedCreatedAt: string;
  formattedPrice: string;
  formattedStart: string;
  formattedFinish: string;
}

interface IAppointmentProductTableProps extends BoxProps {
  appointmentProducts: IAppointmentProductTableItem[];
  currentPage?: number;
  disableProductDetailsButton?: boolean;
  editingLastProduct?: boolean;
  onDelete?: (product: IAppointmentProductTableItem) => void;
  onUpdate?: (product: IAppointmentProductTableItem) => void;
  onSearch?: (value: string) => void;
  onPageChange?: (page: number) => void;
  totalPages?: number;
}

export const AppointmentProductTable = ({
  appointmentProducts,
  currentPage,
  disableProductDetailsButton = false,
  onDelete,
  onSearch,
  onUpdate,
  onPageChange,
  totalPages,
  ...rest
}: IAppointmentProductTableProps): JSX.Element => {
  const { assignment: authenticatedAssignment } = useAuth();

  const [editingProductId, setEditingProductId] = useState<string | undefined>(
    appointmentProducts[0]?.productId,
  );
  const [handlingProduct, setHandlingProduct] = useState<
    IAppointmentProductTableItem | undefined
  >(appointmentProducts[0]);
  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);

  const toggleDeleteConfirmationModal = (
    product?: IAppointmentProductTableItem,
  ): void => {
    setHandlingProduct(product);

    setIsDeleteConfirmationModalVisible((prevState) => !prevState);
  };

  const handleChangeProductQuantity = (quantity: number): void => {
    setHandlingProduct((prevState) =>
      prevState ? { ...prevState, quantity } : undefined,
    );
  };

  const handleChangeProductPrice = (price: number): void => {
    setHandlingProduct((prevState) =>
      prevState
        ? { ...prevState, price, formattedPrice: maskMoney(price) }
        : undefined,
    );
  };

  const handleChangeProductDateRange = (
    field: 'startAt' | 'finishAt',
    value: Date | null,
  ): void => {
    switch (field) {
      case 'startAt':
        setHandlingProduct((prevState) =>
          prevState
            ? {
                ...prevState,
                startAt: value ? value.toISOString() : undefined,
                formattedStart: value ? maskShortDate(value) : '-',
              }
            : undefined,
        );

        break;

      case 'finishAt':
        setHandlingProduct((prevState) =>
          prevState
            ? {
                ...prevState,
                finishAt: value ? value.toISOString() : undefined,
                formattedFinish: value ? maskShortDate(value) : '-',
              }
            : undefined,
        );

        break;

      default:
        break;
    }
  };

  const handleEditProduct = (
    appointmentProduct?: IAppointmentProductTableItem,
  ): void => {
    if (!!onUpdate && !!appointmentProduct) {
      onUpdate(appointmentProduct);
    }

    setEditingProductId(appointmentProduct?.productId);

    setHandlingProduct(appointmentProduct);
  };

  const handleSubmitEditProduct = (
    appointmentProduct?: IAppointmentProductTableItem,
  ): void => {
    if (!!onUpdate && !!appointmentProduct) {
      onUpdate(appointmentProduct);
    }

    setEditingProductId(undefined);

    setHandlingProduct(undefined);
  };

  const handleDeleteProduct = async (
    product?: IAppointmentProductTableItem,
  ): Promise<void> => {
    if (!!onDelete && !!product) {
      onDelete(product);
    }

    toggleDeleteConfirmationModal(undefined);
  };

  return (
    <Box {...rest}>
      {!!handlingProduct && (
        <ConfirmationModal
          isOpen={!!isDeleteConfirmationModalVisible}
          onClose={() => handleDeleteProduct(undefined)}
          onConfirm={() => handleDeleteProduct(handlingProduct)}
          title="Confirmar exclusão"
          message="Deseja realmente remover produto da ordem de serviço?"
        />
      )}

      <HStack align="flex-end" spacing="4">
        <TableFilters searchLabel="Buscar por nome" onSearch={onSearch} />
      </HStack>

      <Table colorScheme="blue" mt={onSearch ? '4' : '0'}>
        <Thead>
          <Tr>
            <Th textAlign="center" w="8">
              Quantidade
            </Th>

            <Th>Produto</Th>

            <Th>Valor</Th>

            <Th>Período</Th>

            <Th textAlign="right" w="8" />
          </Tr>
        </Thead>

        <Tbody>
          {appointmentProducts.map((appointmentProduct) => {
            const isEditing =
              editingProductId === appointmentProduct.productId &&
              !!handlingProduct;

            return (
              <Tr key={appointmentProduct.productId}>
                <Td textAlign="center">
                  {isEditing ? (
                    <NumberInput
                      size="sm"
                      value={handlingProduct.quantity}
                      onChange={(_, quantity) =>
                        handleChangeProductQuantity(quantity)
                      }
                      min={1}
                      max={999}
                    >
                      <NumberInputField textAlign="center" />
                    </NumberInput>
                  ) : (
                    <Text>{appointmentProduct.quantity}</Text>
                  )}
                </Td>

                <Td fontWeight="bold">
                  <Text>{appointmentProduct.name}</Text>
                </Td>

                <Td>
                  {isEditing ? (
                    <MoneyInput
                      size="sm"
                      value={handlingProduct.price}
                      onChange={handleChangeProductPrice}
                    />
                  ) : (
                    <Text>{appointmentProduct.formattedPrice}</Text>
                  )}
                </Td>

                <Td>
                  {isEditing ? (
                    <DateRangePicker
                      startDate={
                        handlingProduct.startAt
                          ? new Date(handlingProduct.startAt)
                          : undefined
                      }
                      endDate={
                        handlingProduct.finishAt
                          ? new Date(handlingProduct.finishAt)
                          : undefined
                      }
                      onStartChange={(date) =>
                        handleChangeProductDateRange('startAt', date)
                      }
                      onEndChange={(date) =>
                        handleChangeProductDateRange('finishAt', date)
                      }
                    />
                  ) : (
                    <>
                      <HStack spacing="3">
                        <Text fontSize="sm" color="gray.400">
                          de:
                        </Text>
                        <Text>{appointmentProduct.formattedStart}</Text>
                      </HStack>
                      <HStack spacing="2">
                        <Text fontSize="sm" color="gray.400">
                          até:
                        </Text>
                        <Text>{appointmentProduct.formattedFinish}</Text>
                      </HStack>
                    </>
                  )}
                </Td>

                <Td textAlign="right">
                  <ButtonGroup>
                    {!disableProductDetailsButton && (
                      <LinkButton
                        px={0}
                        to={{
                          pathname: '/services/products/details',
                          state: {
                            productId: appointmentProduct.productId,
                          },
                        }}
                      >
                        <Icon as={RiEyeLine} fontSize="16" />
                      </LinkButton>
                    )}

                    {!!onUpdate && isEditing ? (
                      <>
                        <IconButton
                          aria-label="save-product"
                          type="button"
                          size="sm"
                          icon={<Icon as={RiCheckLine} fontSize="16" />}
                          colorScheme="green"
                          onClick={() =>
                            handleSubmitEditProduct(handlingProduct)
                          }
                        />

                        <IconButton
                          aria-label="cancel-edit-product"
                          type="button"
                          size="sm"
                          icon={<Icon as={RiCloseLine} fontSize="16" />}
                          onClick={() => handleEditProduct(undefined)}
                        />
                      </>
                    ) : (
                      <>
                        {!!onUpdate &&
                          authenticatedAssignment?.featureGroup.features.some(
                            (feature) =>
                              [
                                'APPOINTMENT_FULL_ACCESS',
                                'APPOINTMENT_WRITE_ACCESS',
                              ].includes(feature.key),
                          ) && (
                            <IconButton
                              aria-label="edit-product"
                              size="sm"
                              type="button"
                              icon={<Icon as={RiEditLine} fontSize="16" />}
                              colorScheme="yellow"
                              color="white"
                              onClick={() =>
                                handleEditProduct(appointmentProduct)
                              }
                            />
                          )}
                      </>
                    )}

                    {!!onDelete &&
                      authenticatedAssignment?.featureGroup.features.some(
                        (feature) =>
                          [
                            'APPOINTMENT_FULL_ACCESS',
                            'APPOINTMENT_WRITE_ACCESS',
                          ].includes(feature.key),
                      ) && (
                        <IconButton
                          aria-label="delete-product"
                          size="sm"
                          type="button"
                          icon={<Icon as={RiDeleteBinLine} fontSize="16" />}
                          colorScheme="red"
                          onClick={() =>
                            toggleDeleteConfirmationModal(appointmentProduct)
                          }
                        />
                      )}
                  </ButtonGroup>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      {!!currentPage && !!onPageChange && !!totalPages && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </Box>
  );
};
